import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  BriefcaseBusiness,
  Building2,
  MapPin,
  CalendarDays,
  Clock3,
} from "lucide-react";

import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { AppButton } from "components/button/AppButton";
import { routePath } from "config/routeMgt/routePath";
import { useGetPendingFeedback } from "features/feedback/hooks/useGetPendingFeedback";
import { useSubmitPendingFeedback } from "features/feedback/hooks/useSubmitPendingFeedback";

type AnswerType = "yes" | "no" | "skip";

export const ConfirmJobOffer = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useGetPendingFeedback();
  const { mutateAsync: submitFeedback } = useSubmitPendingFeedback();

  const [responses, setResponses] = useState<
    Record<
      string,
      {
        answer: AnswerType | null;
        comment: string;
        loading: boolean;
      }
    >
  >({});

  const getUniqueJobs = (jobs: any[]) => {
    if (!jobs) return [];
    const uniqueMap = new Map();
    jobs.forEach((job) => {
      if (!uniqueMap.has(job.id)) {
        uniqueMap.set(job.id, job);
      }
    });
    return Array.from(uniqueMap.values());
  };

  const uniqueData = data ? getUniqueJobs(data) : [];

  const formatDate = (date?: string) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const updateResponse = (
    jobId: string,
    field: "answer" | "comment" | "loading",
    value: string | boolean | AnswerType | null,
  ) => {
    setResponses((prev) => ({
      ...prev,
      [jobId]: {
        answer: prev[jobId]?.answer ?? null,
        comment: prev[jobId]?.comment ?? "",
        loading: prev[jobId]?.loading ?? false,
        [field]: value,
      },
    }));
  };

  const handleSkip = (jobId: string) => {
    setResponses((prev) => ({
      ...prev,
      [jobId]: {
        answer: "skip",
        comment: prev[jobId]?.comment ?? "",
        loading: prev[jobId]?.loading ?? false,
      },
    }));

    setTimeout(() => {
      navigate(`${routePath.home}?skipRedirect=true`);
    }, 200);
  };

  const handleSubmit = async (job: any) => {
    const response = responses[job.id];

    if (response?.answer !== "yes" && response?.answer !== "no") {
      return;
    }

    const decision: "accepted" | "declined" =
      response.answer === "yes" ? "accepted" : "declined";

    const payload = {
      id: job.id,
      decision,
      comment: response.comment,
    };

    try {
      updateResponse(job.id, "loading", true);

      await submitFeedback(payload);

      setResponses((prev) => {
        const newState = { ...prev };
        delete newState[job.id];
        return newState;
      });

      navigate(`${routePath.home}?skipRedirect=true&refreshed=true`);
    } catch (error) {
      console.error(error);
      updateResponse(job.id, "loading", false);
    }
  };

  const optionClass = (active: boolean, disabled?: boolean) =>
    `border rounded-2xl p-4 flex items-center gap-4 transition-all
  ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
  ${
    active
      ? "border-green-500 bg-green-50"
      : "border-[#D0D5DD] hover:border-[#98A2B3]"
  }`;

  const RadioIndicator = ({ active }: { active: boolean }) => (
    <div
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
      ${active ? "border-green-500" : "border-gray-400"}`}
    >
      {active && <div className="w-2.5 h-2.5 rounded-full bg-green-500" />}
    </div>
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-24 text-base font-medium">
          Loading pending feedback...
        </div>
      </DashboardLayout>
    );
  }

  const visibleJobsList = uniqueData;

  if (!visibleJobsList.length) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-24">
          <h2 className="text-2xl font-bold text-[#101828]">
            No Pending Feedback
          </h2>

          <p className="mt-3 text-[#667085]">You're all caught up 🎉</p>

          <button
            onClick={() => navigate(`${routePath.home}?skipRedirect=true`)}
            className="mt-6 rounded-xl bg-[#000B7A] px-6 py-3 text-white"
          >
            Back to Dashboard
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div
            onClick={() => navigate(-1)}
            className="flex w-fit cursor-pointer items-center gap-2"
          >
            <ArrowLeft size={20} />
            <span className="text-xl font-semibold">Back to Job List</span>{" "}
          </div>

          <div className="bg-blue-50 text-blue-700 rounded-lg px-3 py-1.5 text-xs font-medium">
            📋 {visibleJobsList.length} pending
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-center text-3xl font-bold text-[#101828]">
            Confirm Job Offer
          </h1>

          <p className="mt-2 text-center text-base text-[#667085]">
            Help us improve by confirming the status of your applications
          </p>
        </div>

        <div className="space-y-6">
          {visibleJobsList.map((job: any) => {
            const current = responses[job.id] || {
              answer: null,
              comment: "",
              loading: false,
            };

            return (
              <div
                key={job.id}
                className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-sm"
              >
                {/* Top - Job Details Section */}
                <div className="border-b border-[#EAECF0] bg-[#F9FAFB] p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-[#101828]">
                        {job.title}
                      </h2>

                      <p className="mt-1 text-base text-[#667085]">
                        {job.company_name}
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#EAECF0] bg-white px-4 py-3">
                      <div className="flex items-center gap-2 text-[#667085]">
                        <Clock3 size={16} />

                        <span className="text-xs">
                          Posted {formatDate(job.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <div className="flex gap-2">
                      <BriefcaseBusiness
                        size={18}
                        className="mt-0.5 text-indigo-600"
                      />

                      <div>
                        <p className="text-xs text-[#667085]">Employment</p>

                        <h3 className="text-sm font-semibold capitalize">
                          {job.employment_type}
                        </h3>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Building2 size={18} className="mt-0.5 text-purple-600" />

                      <div>
                        <p className="text-xs text-[#667085]">Work Type</p>

                        <h3 className="text-sm font-semibold capitalize">
                          {job.type}
                        </h3>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <MapPin size={18} className="mt-0.5 text-blue-600" />

                      <div>
                        <p className="text-xs text-[#667085]">Location</p>

                        <h3 className="text-sm font-semibold">
                          {job.location_city}, {job.location_country}
                        </h3>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <CalendarDays
                        size={18}
                        className="mt-0.5 text-orange-500"
                      />

                      <div>
                        <p className="text-xs text-[#667085]">Expiry Date</p>

                        <h3 className="text-sm font-semibold">
                          {formatDate(job.expiry_date)}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="my-6 border-b border-[#EAECF0]" />

                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#101828]">
                      Did you get an offer through our platform?
                    </h3>

                    <div className="space-y-3">
                      <label
                        className={optionClass(current.answer === "yes")}
                        onClick={() => updateResponse(job.id, "answer", "yes")}
                      >
                        <RadioIndicator active={current.answer === "yes"} />
                        <span className="text-sm font-medium">
                          Yes, I received an offer for this position
                        </span>
                      </label>

                      <label
                        className={optionClass(current.answer === "no")}
                        onClick={() => updateResponse(job.id, "answer", "no")}
                      >
                        <RadioIndicator active={current.answer === "no"} />
                        <span className="text-sm font-medium">
                          No, this is incorrect
                        </span>
                      </label>

                      <label
                        className={optionClass(current.answer === "skip")}
                        onClick={() => handleSkip(job.id)}
                      >
                        <RadioIndicator active={current.answer === "skip"} />
                        <span className="text-sm font-medium">
                          Skip for now
                        </span>
                      </label>
                    </div>
                  </div>

                  <>
                    <div className="mt-6">
                      <h3 className="mb-2 text-base font-semibold">
                        Additional Comment (Optional)
                      </h3>

                      <textarea
                        rows={4}
                        placeholder="Enter Comment"
                        value={current.comment || ""}
                        onChange={(e) =>
                          updateResponse(job.id, "comment", e.target.value)
                        }
                        className="w-full resize-none rounded-xl border border-[#D0D5DD] p-3 text-sm outline-none transition-all focus:border-[#000B7A]"
                      />
                    </div>

                    <div className="mt-6 flex justify-end">
                      <AppButton
                        label={
                          current.loading
                            ? "Submitting..."
                            : "Submit Confirmation"
                        }
                        handleClick={() => handleSubmit(job)}
                        disabled={
                          current.loading ||
                          (current.answer !== "yes" && current.answer !== "no")
                        }
                      />
                    </div>
                  </>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};
